import './index.css'
import './i18n'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { applyFieldTextSize, readFieldTextSize } from './lib/fieldTextSizeStorage'

applyFieldTextSize(readFieldTextSize())

// Suppress ResizeObserver loop warnings - this is a known browser quirk that's harmless
// This error occurs when ResizeObserver callbacks trigger layout changes that cause more resize events
// It's safe to ignore as it doesn't affect functionality

const resizeObserverErrorPatterns = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop',
]

const isResizeObserverError = (message: any): boolean => {
  if (typeof message === 'string') {
    return resizeObserverErrorPatterns.some((pattern) => message.includes(pattern))
  }
  if (message && typeof message === 'object' && message.message) {
    return resizeObserverErrorPatterns.some((pattern) => message.message.includes(pattern))
  }
  return false
}

// Suppress ResizeObserver errors in console.error (must be first to catch all)
const originalConsoleError = window.console.error
window.console.error = (...args: any[]) => {
  if (isResizeObserverError(args[0])) {
    return
  }
  originalConsoleError.apply(console, args)
}

// Suppress ResizeObserver errors in global error handler
const originalOnError = window.onerror
window.onerror = (message, source, lineno, colno, error) => {
  if (isResizeObserverError(message)) {
    return true // Prevent default error handling
  }
  if (originalOnError) {
    return originalOnError.call(window, message, source, lineno, colno, error)
  }
  return false
}

// Suppress unhandled promise rejections related to ResizeObserver
window.addEventListener('unhandledrejection', (event) => {
  if (isResizeObserverError(event.reason)) {
    event.preventDefault()
  }
})

// Suppress ResizeObserver errors in the error event (for React's error overlay)
// Using capture phase to intercept before React's error boundary
window.addEventListener(
  'error',
  (event) => {
    if (isResizeObserverError(event.message)) {
      event.stopImmediatePropagation()
      event.preventDefault()
      return true
    }
  },
  true
)

const container = document.getElementById('root')
const root = createRoot(container!)
root.render(<App />)

// Production-only PWA service worker (CRA Workbox InjectManifest).
// onUpdate → SKIP_WAITING when online (deferred offline); reload on controllerchange
// when online; apply pending updates + check for new versions on reconnect.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    serviceWorkerRegistration.applyWaitingServiceWorker(registration)
  },
})
